// La disposición de la oficina (S11, brief §5 + crítica A8/M7; desde el 03-11 la Casa en vez del mundo en píxeles): la
// oficina detrás de todo
// y, flotando encima con la misma superficie y el mismo margen (16 px): el panel del equipo a la izquierda
// (con «Contratar» en su cabecera: un solo sitio) y, si hay un chat abierto, su ventana ocupando el resto,
// pegada al panel a 16 px, con el mundo atenuado detrás. Lo usan la página real y las maquetas.
import { useEffect, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import type { ContratadoDTO, SalaDTO } from '../compartido/vocabulario';
import { Casa } from '../casa/Casa';
import { ListaEquipoS12 } from './ListaEquipoS12';
import { RIEL_PX, RielEquipo } from './RielEquipo';
import { useOficinaStore } from '../store';
import type { OrdenMesas } from '../api';

export const MARGEN_PX = 16;
export const PANEL_PX = 336;

/** Lo que ocupa a la izquierda lo que flota (lista abierta o plegada en columna de caras), con sus márgenes. */
export function useIzquierdaPx(): number {
  const plegado = useOficinaStore((s) => s.panelPlegado);
  return MARGEN_PX + (plegado ? RIEL_PX : PANEL_PX) + MARGEN_PX;
}

export function Escenario({
  salas,
  contratados,
  agenteAbierto,
  resaltado,
  alAbrir,
  alCerrarChat,
  alContratar,
  alAjustarSala,
  alOrdenarMesas,
  widgets,
  chat,
}: {
  salas: SalaDTO[];
  contratados: Record<string, ContratadoDTO>;
  agenteAbierto: string | null;
  /** Forzar el resaltado de un agente (maqueta de la tarjeta al pasar el ratón). */
  resaltado?: string | null;
  alAbrir: (agentId: string) => void;
  alCerrarChat: () => void;
  alContratar: (salaId?: string) => void;
  alAjustarSala?: (salaId: string) => void;
  /** 09-10 — ordenar las mesas a mano (solo la página real; las maquetas no ordenan). */
  alOrdenarMesas?: (projectId: string, orden: OrdenMesas) => void;
  /** Rebrand 03-11: la fila de widgets (las pantallas de la pared) arriba de la oficina. */
  widgets?: ReactNode;
  chat: ReactNode;
}) {
  const izquierda = useIzquierdaPx();
  const plegado = useOficinaStore((s) => s.panelPlegado);
  const plegarPanel = useOficinaStore((s) => s.plegarPanel);
  const ajustarPlegado = useOficinaStore((s) => s.ajustarPlegadoAlAncho);
  useEffect(() => {
    const alCambiar = () => ajustarPlegado(window.innerWidth);
    window.addEventListener('resize', alCambiar);
    return () => window.removeEventListener('resize', alCambiar);
  }, [ajustarPlegado]);
  const [sobre, setSobre] = useState<string | null>(null);
  return (
    <div className="relative h-dvh overflow-hidden bg-oficina-fondo font-sans text-oficina-texto antialiased">
      {/* Rebrand 03-11 (ADR D-037): la oficina como la app Casa (tarjetas por estado o por sala) en vez de la escena en
          píxeles. Lo de otras personas sale apagado y con su dueño, sin avisos (lo calcula la Casa). */}
      <Casa
        salas={salas}
        contratados={contratados}
        izquierdaPx={izquierda}
        // Con un chat abierto la oficina queda atenuada detrás: ninguna fila de la lista marca su tarjeta.
        resaltado={resaltado ?? (agenteAbierto ? null : sobre)}
        widgets={widgets}
        alAbrir={alAbrir}
        alContratar={alContratar}
        alAjustarSala={alAjustarSala}
        alOrdenarMesas={alOrdenarMesas}
      />

      {agenteAbierto ? (
        <button type="button" tabIndex={-1} aria-label={t('oficinaV2.chat.cerrar')} onClick={alCerrarChat} className="absolute inset-0 z-20 cursor-default bg-black/50 animate-in fade-in-0" />
      ) : null}

      <div className="pointer-events-none absolute z-30 flex flex-col" style={{ left: MARGEN_PX, top: MARGEN_PX, bottom: MARGEN_PX, width: plegado ? RIEL_PX : PANEL_PX }}>
        <div className="pointer-events-auto flex max-h-full min-h-0 flex-col">
          {plegado ? (
            <RielEquipo contratados={contratados} abierto={agenteAbierto} alAbrir={alAbrir} alContratar={() => alContratar()} alDesplegar={() => plegarPanel(false)} alResaltar={setSobre} />
          ) : (
            <ListaEquipoS12 contratados={contratados} abierto={agenteAbierto} alAbrir={alAbrir} alContratar={() => alContratar()} alResaltar={setSobre} alPlegar={() => plegarPanel(true)} />
          )}
        </div>
      </div>

      {agenteAbierto ? (
        <div className="absolute z-30" style={{ left: izquierda, right: MARGEN_PX, top: MARGEN_PX, bottom: MARGEN_PX }}>
          {chat}
        </div>
      ) : null}
    </div>
  );
}
